import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { makeValidate } from 'mui-rff';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import GeneralInfo from '../PatientRecordPage/BasicInformationModal/GeneralInfo';
import Contact from '../PatientRecordPage/BasicInformationModal/Contact';
import PlaceOfBirth from '../PatientRecordPage/BasicInformationModal/PlaceOfBirth';
import Address from '../PatientRecordPage/BasicInformationModal/Address';
import Coverage from '../PatientRecordPage/BasicInformationModal/Coverage';
import EmergencyContactPerson from '../PatientRecordPage/BasicInformationModal/EmergencyContactPerson';
import PatientsCareTeams from '../PatientRecordPage/BasicInformationModal/PatientsCareTeams';
import PatientCarePlans from '../PatientRecordPage/BasicInformationModal/PatientCarePlans';
import DialogTitle from 'components/Dialogs/DialogTitle';
import InsAccordion from 'pages/PatientRecordPage/BasicInformationModal/InsAccordion';
import CpfAccordion from 'pages/PatientRecordPage/BasicInformationModal/CpfAccordion';
import PlaceOfBirthINS from 'pages/PatientRecordPage/BasicInformationModal/PlaceOfBirthINS';

import {
  Dialog,
  DialogContent,
  Box as MuiBox,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Icon } from 'components/ui';

import { useSelector } from 'react-redux';
import { selectors as authSelectors } from 'services/auth';
import MfaMode from 'lib/enums/mfaMode';
import i18n from 'i18n';
import useGetPatientGroupsForAutocomplete from 'pagesAdmin/SettingsPage/TabPatientGroups/hooks/useGetPatientGroupsForAutocomplete';
import Others from 'pages/PatientRecordPage/BasicInformationModal/Others';
import { languagesAvailableOnMobile } from 'lib/utils/getCountriesList';

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const schema = () =>
  Yup.object().shape({
    name: Yup.object().shape({
      firstName: Yup.array().of(Yup.string().required(i18n.t('Required'))),
      lastName: Yup.string().required(i18n.t('Required')),
      middleName: Yup.string().nullable(),
      suffix: Yup.string().nullable(),
    }),
    birthDate: Yup.date()
      .nullable()
      .typeError(i18n.t('Invalid date'))
      .max(new Date(), i18n.t('Date of birth cannot be in the future')),
    gender: Yup.string().nullable(),
    email: Yup.string()
      .email(i18n.t('Invalid email'))
      .required(i18n.t('Required')),
    phone: Yup.string().when('isMfaRequired', {
      is: (isMfaRequired) => isMfaRequired,
      then: Yup.string()
        .matches(phoneRegExp, i18n.t('Invalid phone number'))
        .required(i18n.t('Required')),
      otherwise: Yup.string()
        .matches(phoneRegExp, i18n.t('Invalid phone number'))
        .notRequired(),
    }),
    birthPlace: Yup.object()
      .nullable()
      .shape({
        country: Yup.string().nullable(),
        address: Yup.string().nullable(),
        zipcode: Yup.string().nullable(),
        city: Yup.string().nullable(),
      }),
    ins: Yup.string().nullable(),
    cpf: Yup.string().nullable(),
    address: Yup.object().nullable().shape({
      country: Yup.string().nullable(),
      address: Yup.string().nullable(),
      zipcode: Yup.string().nullable(),
      city: Yup.string().nullable(),
    }),
    careTeams: Yup.string().nullable(),
    preferredTimeZone: Yup.object()
      .shape({
        name: Yup.string().nullable(),
      })
      .default(undefined)
      .nullable(),
    emergencyContactPerson: Yup.object()
      .nullable()
      .shape({
        firstName: Yup.string().nullable(),
        lastName: Yup.string().nullable(),
        email: Yup.string().email(i18n.t('Invalid email')).nullable(),
        phoneNumber: Yup.string().nullable(),
      }),
    coverage: Yup.object()
      .nullable()
      .shape({
        healthInsuranceId: Yup.string().nullable(),
        healthInsuranceBeneficiary: Yup.string().nullable(),
        healthInsuranceIssuerName: Yup.string().nullable(),
        healthInsuranceCoverageType: Yup.string().nullable(),
        validityStartDate: Yup.date({ accept: `Invalid Date` })
          .typeError('Invalid date')
          .nullable(),
        validityEndDate: Yup.date().when('validityStartDate', {
          is: (val) => val,
          then: Yup.date()
            .typeError('Invalid date')
            .min(
              Yup.ref('validityStartDate'),
              "End date can't be earlier than start date"
            )
            .nullable()
            .required('Required'),
          otherwise: Yup.date({ accept: [`Invalid Date`] }).nullable(),
        }),
      }),
  });

function CreatePatientDialog(props) {
  const { isOpen, close, initialData } = props;
  const { t } = useTranslation();
  const [selectedCareTeamId, setSelectedCareTeamId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchCareTeams, setSearchCareTeams] = useState('');

  const isINS = useSelector(authSelectors.getIsINS);
  const isCPF = useSelector(authSelectors.getIsCPF);
  const workspaceMfaMode = useSelector(authSelectors.getWorkspaceMfaMode);

  const { careTeams } = useSelector((state) => state.user.user); // Only careTeams, that current doctor is a part of can be assigned to a

  const {
    patientGroups,
    loadMore,
    loading,
    setSearch,
  } = useGetPatientGroupsForAutocomplete({
    filter: { careTeamId: selectedCareTeamId },
  });

  const initialValues = useMemo(() => {
    return {
      name: {
        firstName: [''],
        lastName: '',
        middleName: '',
        suffix: '',
      },
      birthDate: null,
      isMfaRequired: [
        MfaMode.OptionalEnabled,
        MfaMode.MandatoryEnabled,
      ].includes(workspaceMfaMode)
        ? true
        : false,
    };
  }, []);

  const handleSave = (values) => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      const newValues = {
        ...values,
        phone: !values.phone
          ? undefined
          : values.phone?.includes('+')
          ? values.phone
          : '+' + values.phone,
        birthDate: values.birthDate
          ? typeof values.birthDate === 'string'
            ? values.birthDate
            : formatISO(values.birthDate, { representation: 'date' })
          : undefined,
        search: undefined,
        careTeams: undefined,
        careTeamIds: values.careTeams ? [values.careTeams] : undefined, // Since current implementation only accepts single value, should be refactored later
        patientGroups: undefined,
        patientGroupIds: values.patientGroups?.map((item) => item.id),
        carePlans: undefined,
        planDefinitionIds: values.carePlans?.map((item) => item.id) || [],
        preferredLanguages: values.preferredLanguages?.map((item) =>
          item?.value ? item.value : item
        ),
        preferredTimeZone: values.preferredTimeZone?.name,
      };
      initialData.createPatient(newValues, close, () => {
        setIsSubmitting(false);
      });
    }
  };

  const changeCareTeam = (value, form) => {
    setSelectedCareTeamId(value.id);
  };

  const onAddFirstName = (form) => {
    form.mutators.push('name.firstName', '');
  };

  const onRemoveFirstName = (form, idx) => {
    form.mutators.remove('name.firstName', idx);
  };

  const validate = useMemo(() => makeValidate(schema()), []);

  return (
    <Dialog
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        close();
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      maxWidth="lg"
    >
      <DialogTitle
        id="customized-dialog-title"
        data-id="customized-dialog-title"
        onClose={close}
      >
        <Typography variant="h4">
          <Icon icon="user-circle" size="1x" mr={10} />
          {t('New patient')}
        </Typography>
      </DialogTitle>

      <DialogContent style={{ width: 720 }}>
        <Form
          data-id="new-patient-form"
          onSubmit={handleSave}
          validate={validate}
          initialValues={initialValues}
          mutators={{ ...arrayMutators }}
          render={({ handleSubmit, form, invalid, values: formValues }) => (
            <form onSubmit={handleSubmit}>
              <GeneralInfo
                formValues={formValues}
                onAddFirstName={onAddFirstName}
                onRemoveFirstName={onRemoveFirstName}
                form={form}
                isBirthDateRequired={false}
              />

              <MuiBox
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <Contact isPhoneRequired={formValues?.isMfaRequired} />
                {isINS ? (
                  <PlaceOfBirthINS isRequired={false} />
                ) : (
                  <PlaceOfBirth isRequired={false} />
                )}
                {isINS && <InsAccordion isRequired={false} />}
                {isCPF && <CpfAccordion isRequired={false} />}
                <Address isPreferredTimeZoneRequired={false} />
                <EmergencyContactPerson />
                <Coverage form={form} />
                <PatientsCareTeams
                  onCareTeamsChange={changeCareTeam}
                  careTeams={careTeams}
                  form={form}
                  selectedCareTeamId={selectedCareTeamId}
                  showGroups
                  patientGroups={patientGroups}
                  loadMoreGroups={loadMore}
                  loadingGroups={loading}
                  setSearchGroups={setSearch}
                  setSearchCareTeams={setSearchCareTeams}
                  isRequired={false}
                />
                <PatientCarePlans form={form} />
                <Others languages={languagesAvailableOnMobile} form={form} />
              </MuiBox>

              <MuiBox
                display="flex"
                justifyContent="center"
                margin={'10px'}
                width={'100%'}
              >
                <LoadingButton
                  data-id="create-patient"
                  disabled={invalid}
                  key="save"
                  type="submit"
                  color="primary"
                  variant="contained"
                  loading={isSubmitting}
                >
                  <Icon icon="user-plus" size="1x" mr={10} />
                  {t('Create patient')}
                </LoadingButton>
              </MuiBox>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
}

export default CreatePatientDialog;
